import formatNumber from 'src/public/formatNumber/formatNumber';

import type {IAnalytics, IReports} from '@sail/observability';
import type {FormatBasisPointsOptions} from 'src/public/formatBasisPoints/types';
import type {IntlConfig, SupportedLocale} from 'src/types';

const abbreviations: Record<SupportedLocale, string> = {
  'ar-SA': 'نقطة أساس (bps)',
  'bg-BG': 'б.т. (bps)',
  'bn-IN': 'bps',
  'ca-ES': 'bps',
  'cs-CZ': 'bps',
  'cy-GB': 'bps',
  'da-DK': 'bps',
  'de-DE': 'bps',
  'el-GR': 'bps',
  'en-AU': 'bps',
  'en-CA': 'bps',
  'en-GB': 'bps',
  'en-IE': 'bps',
  'en-IN': 'bps',
  'en-NL': 'bps',
  'en-NZ': 'bps',
  'en-SG': 'bps',
  'en-US': 'bps',
  'en-US-x-MESSAGE-ID': 'bps',
  'en-US-x-STAR': 'bps',
  'en-US-x-WIDE': 'bps',
  'es-419': 'bps',
  'es-ES': 'bps',
  'et-EE': 'bps',
  'eu-ES': 'bps',
  'fi-FI': 'bps',
  'fil-PH': 'bps',
  'fr-CA': 'pbs',
  'fr-FR': 'bps',
  'gl-ES': 'bps',
  'gu-IN': 'bps',
  'he-IL': 'bps',
  'hi-IN': 'bps',
  'hr-HR': 'bps',
  'hu-HU': 'bps',
  'id-ID': 'bps',
  'it-IT': 'bps',
  'ja-JP': 'bps',
  'kn-IN': 'bps',
  'ko-KR': 'bps',
  'lt-LT': 'bps',
  'lv-LV': 'bps',
  'ml-IN': 'bps',
  'mr-IN': 'bps',
  'ms-MY': 'bps',
  'mt-MT': 'bps',
  'nb-NO': 'bps',
  'nl-BE': 'bps',
  'nl-NL': 'bps',
  'nn-NO': 'bps',
  'or-IN': 'bps',
  'pa-IN': 'bps',
  'pa-Guru-IN': 'bps',
  'pl-PL': 'bps',
  'pt-BR': 'bps',
  'pt-PT': 'bps',
  'ro-RO': 'bps',
  'ru-RU': 'б.п. (bps)',
  'sk-SK': 'bps',
  'sl-SI': 'bps',
  'sv-SE': 'bps',
  'ta-IN': 'bps',
  'te-IN': 'bps',
  'th-TH': 'bps',
  'tr-TR': 'bps',
  'vi-VN': 'bps',
  'zh-Hans': 'bps',
  'zh-Hans-CN': 'bps',
  'zh-Hant-HK': 'bps',
  'zh-Hant-TW': 'bps',
};

/**
 * @example Basic {{include "./examples/formatBasisPoints.basic.tsx"}}
 *
 * @see https://sail.stripe.me/apis/intl/useIntl#format-number
 */
export default function formatBasisPoints(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The numeric value to be formatted. */
  number: number,
  /** An object with options to customize the formatting. */
  options?: FormatBasisPointsOptions,
): string {
  const abbr = abbreviations[config.locale] ?? abbreviations['en-US'];
  return formatNumber(config, reports, analytics, number, options) + abbr;
}

export type FormatBasisPoints = typeof formatBasisPoints;

export type FormatBasisPointsDeclarative = (
  number: Parameters<FormatBasisPoints>[3],
  options?: Parameters<FormatBasisPoints>[4],
) => ReturnType<FormatBasisPoints>;
